import {Injectable} from '@angular/core';
import {CartAdvancedInterface} from '../cart/cart.interface';
import {
  BaseCheckout,
  BookingGet,
  BookingId,
  BookingItemLight,
  BookingOptions,
  BookingPost,
  BookingPut,
  BookingSearch,
  BookingService as Api,
  BookingSortBy,
  BookingStatus,
  BookingTransaction,
  BookingType,
  CartTotal,
  Discount,
  EventPricing,
  LegacyTicket,
  PromocodeRequest,
  Response,
  Scrap,
  TicketBarcodes,
  TicketIds,
  TicketPrice,
  TicketPriceType,
  TicketsRequest,
  TicketsResponse,
  Waybill,
  WaybillGet
} from '../../generated/sales-terminal-client';
import {Observable, tap} from 'rxjs';
import {AuthService} from '../auth/auth.service';
import {LanguageService} from '../language/language.service';
import {SnackService} from '../snack/snack.service';
import {finalize} from 'rxjs/operators';
import {PriceInCentsPipe} from '../../pipes/priceInCents/priceInCents.pipe';
import {TicketPricePipe} from '../../pipes/ticketPrice/ticketPrice.pipe';
import {PricePipe} from '../../pipes/price/price.pipe';
import {StorageService} from '../storage/storage.service';
import {CartService} from '../cart/cart.service';
import {cloneDeep} from 'lodash-es';
import {PageableSearch} from '../../components/scroll-load-component/scroll-load.component';
import {CartBaseService, CartTypes} from '../cart/cart-base.service';
import {CustomTicket} from '../../modules/sales-terminal/cart/table/cart-table.component';
import {EventService} from '../event/event.service';
import {WebshopCustomer} from '../../generated/customer-client';
import {RouteService} from '../route/route.service';
import {SortOrderEnum} from '../../generated/sales-terminal-client';
import {SelfServiceEventService} from '../self-service/event/self-service-event.service';
import {MAX_TICKETS_IN_CART} from "../../modules/sales-terminal/sales-terminal.constants";
import {CentrePropertyService} from "../centre-property/centre-property.service";

export interface BookingSearchFilter {
  customerUuid?: string;
  bookingNumber?: string;
  eventId?: number;
  date?: number;
  dateEnd?: number;
  dueDate?: number;
  dueDateEnd?: number;
  type?: BookingType;
  status?: BookingStatus;
  invoiceNumber?: number;
  legalPersonId?: number;
}
export interface BookingSearchData extends PageableSearch {
  sortBy?: BookingSortBy,
  sortOrder?: SortOrderEnum,
  l?: string;
  filters?: BookingSearchFilter;
}

@Injectable()
export class BookingsService extends CartBaseService implements CartAdvancedInterface<BookingGet> {
  public override currentBooking: BookingGet;
  public currentBookingMap: Record<number, BookingGet> = {};
  public openedFromList: boolean;
  public webShopCustomer: WebshopCustomer;
  public eventPricing: EventPricing;
  public eventPricingMap: Record<number, EventPricing> = {};

  constructor(private _api: Api,
              private cartService: CartService,
              protected authService: AuthService,
              protected centrePropertyService: CentrePropertyService,
              protected languageService: LanguageService,
              protected snackService: SnackService,
              protected storageService: StorageService,
              protected eventService: EventService,
              protected selfServiceEventService: SelfServiceEventService,
              protected routeService: RouteService,
              protected priceInCentsPipe: PriceInCentsPipe,
              protected ticketPricePipe: TicketPricePipe,
              protected pricePipe: PricePipe) {
    super(authService, centrePropertyService, languageService, snackService, storageService, eventService, selfServiceEventService, routeService, priceInCentsPipe, ticketPricePipe, pricePipe);
  }

  public get isSelfService(): boolean {
    return this.cartType === CartTypes.SelfService;
  }

  public calculateMaxTickets(max: number): number {
    if (this.isSelfService) {
      const maxTicketsLeft = MAX_TICKETS_IN_CART - (this.cart?.total?.ticketQuantity || 0);
      if (max > maxTicketsLeft) {
        max = maxTicketsLeft;
      }
    }
    return max;
  }

  public override updateCurrentBooking(newBooking: BookingGet): void {
    this.currentBooking = newBooking;
    this.currentBookingMap[newBooking.id] = newBooking;
    this.cart = this.currentBooking?.bookingCart;
    if (!newBooking) {
      this.cartService.clearBooking();
    }
  }

  public getBooking(bookingId: number = this.currentBooking.id): Observable<BookingGet> {
    return this._api.findBookingById(bookingId).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }
  public getBookingOptions(tickets: TicketIds): Observable<BookingOptions> {
    return this._api.bookingOptionsGet(tickets).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public subTotals(ticketIds: TicketIds, bookingId: number = this.currentBooking.id): Observable<CartTotal> {
    return this._api.bookingSubtotals(bookingId, ticketIds).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public updateCart(newData: BookingGet): void {
    this.updateCurrentBooking(newData);
  }

  public retryCart(selectTickets = false, eventId: number = null, bookingId: number = this.currentBooking.id): void {
    this.isLoading = true;
    this.getBooking(bookingId).pipe(
      finalize(() => this.isLoading = false)
    ).subscribe((booking) => {
      this.updateCurrentBooking(booking);
      if (selectTickets) {
        this.selectAllTickets();
      }
      this.updateSelectionMap();
    });
  }

  public addGiftCodes(ticketBarcodes: TicketBarcodes, bookingId: number = this.currentBooking.id): Observable<BookingGet> {
    return this._api.bookingIdGiftPost(bookingId, ticketBarcodes, this.languageService.selectedLang).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }
  public removeGiftCodes(ticketBarcodes: TicketBarcodes, bookingId: number = this.currentBooking.id): Observable<BookingGet> {
    return this._api.bookingIdGiftPut(bookingId, ticketBarcodes, this.languageService.selectedLang).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public applyDiscount(discountValue?: number): void {
    this.isLoading = true;
    this.updateDiscount({discountPercent: discountValue || 0}).pipe(
      finalize(() => this.isLoading = false)
    ).subscribe((booking) => this.updateCart(booking));
  }
  public updateDiscount(discount: Discount, bookingId: number = this.currentBooking.id): Observable<BookingGet> {
    return this._api.bookingIdDiscountPut(bookingId, discount, this.languageService.selectedLang).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public addPromocode(promocodeRequest: PromocodeRequest, bookingId: number = this.currentBooking.id): Observable<BookingGet> {
    return this._api.addBookingPromocode(bookingId, promocodeRequest, this.languageService.selectedLang).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }
  public removePromocode(promocodeRequest: PromocodeRequest, bookingId: number = this.currentBooking.id): Observable<BookingGet> {
    return this._api.removeBookingPromocode(bookingId, promocodeRequest, this.languageService.selectedLang).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public updateSubTotals(): void {
    this.isLoading = true;
    if (this.hasSelectedTickets) {
      this.subTotals({tickets: this.getSelectedTicketIds}).pipe(
        tap({error: () => this.retryCart()}),
        finalize(() => this.isLoading = false)
      ).subscribe((cartTotal) => this.finishSubTotals(cartTotal));
    } else {
      this.finishSubTotals(cloneDeep(this._emptyTotals));
    }
  }
  private finishSubTotals(total: CartTotal): void {
    this.setSubTotals(total);
    this.isLoading = false;
  }


  public addBooking(data: BookingPost): Observable<BookingId> {
    return this._api.bookingAdd(data, this.languageService.selectedLang).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }
  public changeBooking(data: BookingPut, bookingId: number = this.currentBooking.id): Observable<BookingGet> {
    return this._api.updateBookingById(bookingId, data, this.languageService.selectedLang).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }
  public releaseBooking(baseCheckout: BaseCheckout, bookingId = this.currentBooking.id): Observable<BookingTransaction> {
    return this._api.bookingCheckout(bookingId, baseCheckout, this.languageService.selectedLang).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }
  public cancelBooking(toCart: boolean, bookingId: number = this.currentBooking.id): Observable<BookingId> {
    return this._api.cancelBookingById(bookingId, toCart).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }
  public splitBooking(splitSelection: TicketIds, bookingId: number = this.currentBooking.id): Observable<BookingId> {
    return this._api.bookingSplit(bookingId, splitSelection, this.languageService.selectedLang).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }
  public scrapBooking(scrap: Scrap, bookingId: number = this.currentBooking.id): Observable<BookingId> {
    return this._api.bookingScrap(bookingId, scrap).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public search(data: BookingSearchData): Observable<BookingSearch> {
    return this._api.findBookings(
      data.l || this.languageService.selectedLang,
      data.page,
      data.pageSize,
      data.sortBy,
      data.sortOrder,
      data.filters?.customerUuid,
      data.filters?.bookingNumber || null,
      data.filters?.eventId,
      data.filters?.date,
      data.filters?.dateEnd,
      data.filters?.dueDate,
      data.filters?.dueDateEnd,
      data.filters?.type,
      data.filters?.status,
      data.filters?.invoiceNumber || null,
      data.filters?.legalPersonId
    ).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }
  public searchSimple(bookingNumber: string, bookingId: number = this.currentBooking.id): Observable<BookingItemLight[]> {
    return this._api.findLeightweightBookings(bookingNumber, bookingId).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public override updateTicketCustomPrice(eventId: number, ticket: CustomTicket): void {
    this.isLoading = true;
    super.updateTicketCustomPrice(eventId, ticket);
    this.updateTicketPrice(eventId, this.customPriceRequest).pipe(
      finalize(() => this.isLoading = false)
    ).subscribe();
  }
  public updateTicketPrice(eventId: number, tickets: TicketPrice[], bookingId: number = this.currentBooking.id): Observable<BookingGet> {
    return this._api.updateBookedTicketPrice(bookingId, tickets, this.languageService.selectedLang).pipe(
      tap({
        error: (e) => {
          this.snackService.showBackendError(e);
          this.retryCart();
        },
        next: (booking: BookingGet) => {
          this.updateCart(booking);
        }
      })
    );
  }

  public override updateTicketPriceType(eventId: number, ticket: LegacyTicket): void {
    this.isLoading = true;
    super.updateTicketPriceType(eventId, ticket);
    if (this.priceTypeRequest.length) {
      this.updateTicketPriceTypes(eventId, this.priceTypeRequest).pipe(
        finalize(() => this.isLoading = false)
      ).subscribe();
    }
  }
  public updateTicketPriceTypes(eventId: number, tickets: TicketPriceType[], bookingId: number = this.currentBooking.id): Observable<BookingGet> {
    return this._api.updateBookedTicketPriceType(bookingId, tickets, this.languageService.selectedLang).pipe(
      tap({
        error: (e) => {
          this.snackService.showBackendError(e);
          this.retryCart();
        },
        next: (booking: BookingGet) => {
          this.updateCart(booking);
        }
      })
    );
  }

  public addTickets(ticketIds: TicketIds, bookingId: number = this.currentBooking.id): Observable<BookingId> {
    return this._api.addTicketsToBooking(bookingId, ticketIds, this.languageService.selectedLang).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }
  public removeTickets(ticketIds: number[] = this.getSelectedTicketIds): void {
    this.removeBookingTickets(ticketIds);
  }
  private removeBookingTickets(ticketIds: number[]): void {
    this.isLoading = true;
    this.cancelBookingTickets({tickets: ticketIds}).pipe(
      tap({error: (e) => this.retryCart()}),
      finalize(() => this.isLoading = false)
    ).subscribe((booking) => {
      this.updateCurrentBooking(booking);
      this.updateSelectionMap();
    });
  }
  public cancelBookingTickets(ticketIds: TicketIds, toCart: boolean = false, bookingId: number = this.currentBooking.id): Observable<BookingGet> {
    return this._api.deleteBookingTicketsById(bookingId, toCart, this.languageService.selectedLang, ticketIds).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  // TEMPORARY PLACEHOLDERS FOR SELF-SERVICE CART - TO BE IMPLEMENTED WHEN SELF-SERVICE BOOKINGS COME
  public removeTicketTypes(eventId: number, ticketsRequests: TicketsRequest[]): void {}
  public setUnassignedTickets(eventId: number, ticketsRequests: TicketsRequest[]): Observable<TicketsResponse> {return null;}
  public updateCartForEvent(inEventId: number): void {}
  public addSelfServicePromocode(promocodeRequest: PromocodeRequest, eventId: number): Observable<EventPricing> {return null;}
  public removeSelfServicePromocode(eventId: number): Observable<EventPricing> {return null;}
  public addSelfServiceGiftCode(giftCode: string): Observable<BookingGet> {return null;}
  public removeSelfServiceGiftCode(giftCode: string): Observable<BookingGet> {return null;}

  public getInvoice(customerUuid: string, legalPersonId: number, bookingId: number = this.currentBooking.id): Observable<Blob> {
    return this._api.bookingInvoiceById(bookingId, this.languageService.selectedLang, customerUuid, legalPersonId).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }
  public getWaybill(bookingId: number = this.currentBooking.id): Observable<WaybillGet> {
    return this._api.getBookingWaybill(bookingId).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }
  public addWaybill(waybill: Waybill, bookingId: number = this.currentBooking.id): Observable<Response> {
    return this._api.addBookingWaybill(bookingId, waybill).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public updateInternalComment(internalComment: string, bookingId: number = this.currentBooking.id): Observable<BookingGet> {
    return this._api.bookingIdInternalcommentPut(bookingId, {internalComment: internalComment}).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public markAsPaid(bookingId: number = this.currentBooking.id, paymentDate: number): Observable<BookingGet> {
    return this._api.bookingPay(bookingId, paymentDate).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }
  public markAsUnpaid(bookingId: number = this.currentBooking.id): Observable<BookingGet> {
    return this._api.cancelBookingPayment(bookingId).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }
}
